import API_CONFIG from '../config/api';
import { fetchAuthSession } from 'aws-amplify/auth';

// =============================================================================
// Response shape primitives — every list endpoint returns { items, metadata }
// per CLAUDE.md API standards; single-resource endpoints return { data, metadata }.
// Mirrors the pattern in src/api/tax.ts.
// =============================================================================

export interface ResponseMetadata {
  total?: number;
  timestamp: string;
  requestId?: string;
}

export interface ListResponse<T> {
  items: T[];
  metadata: ResponseMetadata;
}

export interface SingleResponse<T> {
  data: T;
  metadata: ResponseMetadata;
}

// =============================================================================
// Endpoint types — field sets pinned in spec-driven/measured_performance_ledger/
// wire-contracts.md; keep in sync with PerformanceRollupRow / PerformanceLensResponse /
// PerformanceDrilldownResponse on the API side.
// =============================================================================

export type PerformanceRollupsGroupBy = 'day' | 'strategy' | 'product';

export interface PerformanceRollupRow {
  day: string | null;
  strategyType: string | null;
  productId: string | null;
  grossProceeds: number | null;
  totalFees: number | null;
  netPnl: number | null;
  tradeCount: number;
  coverageGap: boolean;
}

export interface PerformanceRollupsParams {
  groupBy?: PerformanceRollupsGroupBy;
  from?: string;
  to?: string;
  productId?: string;
}

export interface PerformanceLensResponse {
  strategyType: string;
  sampleSize: number;
  actualWinRate: number | null;
  averageWin: number | null;
  averageLoss: number | null;
  realizedRewardRisk: number | null;
  breakevenWinRate: number | null;
  expectedValuePerTrade: number | null;
  clearsBreakeven: boolean;
}

export interface PerformanceLensParams {
  productId?: string;
  from?: string;
  to?: string;
}

export interface PerformanceDrilldownFill {
  clientOrderId: string;
  side: string;
  status: string;
  filledValue: number;
  totalFees: number;
  totalValueAfterFees: number;
}

export interface PerformanceDrilldownTrade {
  tradeId: string;
  day: string | null;
  grossProceeds: number;
  totalFees: number;
  netPnl: number;
  isWinner: boolean;
  fills: PerformanceDrilldownFill[];
}

export interface PerformanceDrilldownResponse {
  tradeGroupId: string;
  strategyType: string;
  productId: string;
  coverageGap: boolean;
  trades: PerformanceDrilldownTrade[];
}

// =============================================================================
// Auth + fetch primitive — mirrors the apiFetch pattern from src/api/tax.ts,
// bound to API_CONFIG.performanceRollupsBaseUrl.
// =============================================================================

async function getAuthToken(): Promise<string | undefined> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString();
  } catch (error) {
    console.error('Failed to get auth token:', error);
    return undefined;
  }
}

async function apiFetch<T>(endpoint: string): Promise<T> {
  const url = `${API_CONFIG.performanceRollupsBaseUrl}${endpoint}`;

  const authToken = await getAuthToken();
  if (!authToken) {
    throw new Error('Authentication required. Please log in.');
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`Performance Rollups API Error: ${response.status} - ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error) {
      if (error.name === 'AbortError') throw new Error('Request timeout - please try again');
      throw error;
    }
    throw new Error('Unknown error occurred');
  }
}

function buildQuery(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.append(key, String(value));
    }
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

// =============================================================================
// Endpoint functions
// =============================================================================

export async function getPerformanceRollups(
  params: PerformanceRollupsParams = {}
): Promise<PerformanceRollupRow[]> {
  const response = await apiFetch<ListResponse<PerformanceRollupRow>>(
    `/v1/performance-rollups${buildQuery(params as unknown as Record<string, unknown>)}`
  );
  return response.items;
}

export async function getPerformanceLens(
  strategyType: string,
  params: PerformanceLensParams = {}
): Promise<PerformanceLensResponse> {
  const response = await apiFetch<SingleResponse<PerformanceLensResponse>>(
    `/v1/performance-rollups/lens/${encodeURIComponent(strategyType)}${buildQuery(params as unknown as Record<string, unknown>)}`
  );
  return response.data;
}

export async function getPerformanceDrilldown(tradeGroupId: string): Promise<PerformanceDrilldownResponse> {
  const response = await apiFetch<SingleResponse<PerformanceDrilldownResponse>>(
    `/v1/performance-rollups/drilldown/${encodeURIComponent(tradeGroupId)}`
  );
  return response.data;
}
